<template>
  <el-dialog
    title="分类管理"
    :visible.sync="dialogVisiClass"
    width="700px"
    :before-close="handleCancel"
    :close-on-click-modal="false"
  >
    <div class="dialog-body" v-loading="loading">
      <div class="tree-list" v-if="flatList.length > 0">
        <div
          v-for="(item, index) in flatList"
          :key="item._key"
          class="tree-item"
          :class="{
            'drag-over': dragOverIndex === index && dragIndex !== index,
            'drag-source': dragIndex === index,
            editing: item._editing,
          }"
          :style="{ paddingLeft: 12 + item.depth * 28 + 'px' }"
          @dragover="onDragOver($event, index)"
          @drop="onDrop($event, index)"
          @dragend="onDragEnd"
        >
          <span
            class="drag-handle"
            :class="{ 'no-drag': item.node._isNew }"
            draggable="true"
            @dragstart="onDragStart($event, index)"
            >☰</span
          >

          <span
            class="expand-icon"
            :class="{ placeholder: !item.hasChildren }"
            @click="item.hasChildren && toggleExpand(item.node)"
          >
            {{ item.hasChildren ? (item.expanded ? "▼" : "▶") : "" }}
          </span>

          <el-form
            :model="item.node"
            :rules="editRules"
            :ref="'editForm_' + item._key"
            v-if="item._editing"
            class="edit-form-inline"
          >
            <el-form-item prop="className" class="edit-form-item">
              <el-input
                v-model="item.node.className"
                size="mini"
                maxlength="12"
                clearable
                ref="editInput"
                @input="
                  item.node.className = item.node.className.replace(/\s/g, '')
                "
              />
            </el-form-item>
          </el-form>

          <span
            v-else
            class="node-name"
            :class="{ deleted: item.node._isDeleted }"
            @dblclick="startEdit(item)"
          >
            {{ item.node.className }}
          </span>

          <span class="actions" v-if="item._editing">
            <el-button size="mini" type="text" @click="finishEdit(item)"
              >确定</el-button
            >
            <el-button size="mini" type="text" @click="cancelEdit(item)"
              >取消</el-button
            >
          </span>

          <span class="actions" v-else>
            <i
              v-if="item.depth < 2"
              class="el-icon-plus"
              title="新增下级分类"
              @click="addChild(item.node)"
            ></i>
            <i class="el-icon-edit" title="编辑" @click="startEdit(item)"></i>
            <i
              class="el-icon-delete"
              title="删除"
              @click="deleteNode(item.node)"
            ></i>
          </span>
        </div>
      </div>
      <div v-else class="empty-tip">暂无分类数据</div>

      <div class="add-first-area">
        <el-button
          v-if="!showAddFirstInput"
          type="text"
          icon="el-icon-plus"
          size="small"
          @click="openAddFirstInput"
          >新增一级分类</el-button
        >

        <div class="add-first-input-row" v-if="showAddFirstInput">
          <el-form
            :model="addFirstForm"
            :rules="addFirstRules"
            ref="addFirstForm"
            class="add-first-form"
          >
            <el-form-item prop="name">
              <el-input
                v-model="addFirstForm.name"
                size="mini"
                maxlength="12"
                show-word-limit
                clearable
                placeholder="输入分类名称"
                ref="addFirstInput"
                @input="
                  addFirstForm.name = addFirstForm.name.replace(/\s/g, '')
                "
              />
            </el-form-item>
          </el-form>
          <span class="action-btns">
            <el-button size="mini" type="text" @click="confirmAddFirstLevel"
              >确定</el-button
            >
            <el-button size="mini" type="text" @click="cancelAddFirstLevel"
              >取消</el-button
            >
          </span>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleCancel">取消</el-button>
      <el-button
        size="small"
        type="primary"
        @click="handleSave"
        :loading="saving"
        >保存</el-button
      >
    </div>
  </el-dialog>
</template>

<script>
import { selectAllClass, saveClass } from "@/api/sciptLibrary/index";

let uidCounter = 0;
function genUid() {
  return "new_" + ++uidCounter + "_" + Date.now();
}

export default {
  props: {
    dialogVisiClass: {
      type: Boolean,
      default: false,
    },
    popupConfig: {
      type: Object,
      default: () => {
        return {
          title: "",
          scriptId: "",
        };
      },
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      treeData: [],
      treeKey: 0,
      showAddFirstInput: false,
      addFirstForm: { name: "" },
      addFirstRules: {
        name: [
          { required: true, message: "分类名称不能为空", trigger: "blur" },
          { max: 12, message: "分类名称不能超过12个字符", trigger: "blur" },
        ],
      },
      // 树节点行内编辑校验（Element rules，失焦触发）
      editRules: {
        className: [
          { required: true, message: "分类名称不能为空", trigger: "blur" },
          { max: 12, message: "分类名称不能超过12个字符", trigger: "blur" },
        ],
      },
      dragIndex: -1,
      dragOverIndex: -1,
    };
  },
  computed: {
    flatList() {
      const result = [];
      const treeKey = this.treeKey;
      const traverse = (nodes, depth, parentExpanded) => {
        if (!parentExpanded) return;
        nodes.forEach((node) => {
          if (node._isDeleted) return;
          const children = node.children || [];
          const hasChildren = children.some((c) => !c._isDeleted);
          const expanded = node._expanded !== false;
          result.push({
            node,
            depth,
            hasChildren,
            expanded,
            _editing: !!node._editing,
            _key: node._uid != null ? node._uid : node.id,
          });
          if (hasChildren) {
            traverse(children, depth + 1, parentExpanded && expanded);
          }
        });
      };
      traverse(this.treeData, 0, true);
      return result;
    },
  },
  mounted() {
    this.loadTree();
  },
  methods: {
    async loadTree() {
      this.loading = true;
      try {
        const res = await selectAllClass();
        if (res.error.code === 200 && Array.isArray(res.data)) {
          this.treeData = this.buildTree(res.data);
        } else {
          this.treeData = [];
        }
      } catch (e) {
        this.treeData = [];
      }
      this.loading = false;
    },

    buildTree(flatList) {
      const map = {};
      const roots = [];
      flatList.forEach((item) => {
        map[item.id] = {
          ...item,
          children: [],
          _uid: item.id,
          _expanded: true,
          _editing: false,
          _isNew: false,
          _isDeleted: false,
          _isModified: false,
          _originalName: item.className,
          orderNum: item.orderNum != null ? item.orderNum : 0,
        };
      });

      flatList.forEach((item) => {
        const node = map[item.id];

        const isRoot =
          item.parentId === -1 ||
          item.parentId === "-1" ||
          item.parentId === 0 ||
          item.parentId === "0" ||
          item.parentId == null ||
          !map[item.parentId];

        if (isRoot) {
          node.parentId = -1;
          roots.push(node);
        } else {
          map[item.parentId].children.push(node);
        }
      });

      roots.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));
      roots.forEach((root) => this.sortChildren(root));
      return roots;
    },

    sortChildren(node) {
      if (node.children && node.children.length > 0) {
        node.children.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));
        node.children.forEach((child) => this.sortChildren(child));
      }
    },

    findNodeById(id) {
      const search = (nodes) => {
        for (const node of nodes) {
          if (node.id === id || node._uid === id) return node;
          if (node.children && node.children.length) {
            const found = search(node.children);
            if (found) return found;
          }
        }
        return null;
      };
      return search(this.treeData);
    },

    addFirstLevel() {
      const name = this.addFirstForm.name.trim();
      const activeCount = this.treeData.filter((n) => !n._isDeleted).length;
      const newNode = {
        id: null,
        _uid: genUid(),
        className: name,
        parentId: -1,
        level: 1,
        orderNum: activeCount,
        children: [],
        _expanded: true,
        _editing: false,
        _isNew: true,
        _isDeleted: false,
        _originalName: "",
      };
      this.treeData.push(newNode);
      this.addFirstForm.name = "";
      this.showAddFirstInput = false;
    },

    confirmAddFirstLevel() {
      this.$refs.addFirstForm.validate((valid) => {
        if (valid) {
          this.addFirstLevel();
        } else {
          return false;
        }
      });
    },

    cancelAddFirstLevel() {
      this.showAddFirstInput = false;
      this.addFirstForm.name = "";
      this.$refs.addFirstForm.resetFields();
    },

    openAddFirstInput() {
      this.showAddFirstInput = true;
      this.addFirstForm.name = "";
      this.$nextTick(() => {
        if (this.$refs.addFirstForm) {
          this.$refs.addFirstForm.clearValidate();
        }
        const input = this.$refs.addFirstInput;
        const el = Array.isArray(input) ? input[0] : input;
        if (el) {
          el.focus();
        }
      });
    },

    addChild(parentNode) {
      const newChild = {
        id: null,
        _uid: genUid(),
        className: "",
        parentId: parentNode.id || parentNode._uid,
        level: (parentNode.level || 1) + 1,
        orderNum: (parentNode.children || []).filter((c) => !c._isDeleted)
          .length,
        children: [],
        _expanded: true,
        _editing: true,
        _isNew: true,
        _isDeleted: false,
        _originalName: "",
      };
      if (!parentNode.children) {
        this.$set(parentNode, "children", []);
      }
      parentNode.children.push(newChild);
    },

    toggleExpand(node) {
      this.$set(node, "_expanded", !(node._expanded !== false));
    },

    startEdit(item) {
      this.$set(item.node, "_editing", true);
      item._editing = true;
      this.$nextTick(() => {
        const inputs = this.$refs.editInput;
        const input = Array.isArray(inputs)
          ? inputs[inputs.length - 1]
          : inputs;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },

    getEditFormRef(item) {
      const ref = this.$refs["editForm_" + item._key];
      return Array.isArray(ref) ? ref[0] : ref;
    },

    finishEdit(item) {
      const form = this.getEditFormRef(item);
      if (!form) return;
      form.validate((valid) => {
        if (!valid) return;
        if (
          item.node.className !== item.node._originalName &&
          !item.node._isNew
        ) {
          this.$set(item.node, "_isModified", true);
        }
        this.$set(item.node, "_editing", false);
        item._editing = false;
      });
    },

    cancelEdit(item) {
      if (item.node._isNew) {
        this.removeNodeFromParent(item.node);
        return;
      }
      item.node.className = item.node._originalName;
      this.$set(item.node, "_editing", false);
      item._editing = false;
    },

    removeNodeFromParent(node) {
      const pid = node.parentId;
      if (this.normalizeParentId(pid) === -1) {
        const idx = this.treeData.indexOf(node);
        if (idx > -1) this.treeData.splice(idx, 1);
      } else {
        const parent = this.findNodeById(pid);
        if (parent && parent.children) {
          const idx = parent.children.indexOf(node);
          if (idx > -1) parent.children.splice(idx, 1);
        }
      }
    },

    deleteNode(node) {
      const activeChildren = (node.children || []).filter((c) => !c._isDeleted);
      if (activeChildren.length > 0) {
        this.$confirm(
          "该分类下还有子分类，删除后子分类将自动归属上级，确认删除？",
          "提示",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          },
        )
          .then(() => {
            this.promoteChildrenToParent(node, activeChildren);
            if (node._isNew) {
              this.removeNodeFromParent(node);
            } else {
              this.$set(node, "_isDeleted", true);
              this.$set(node, "_expanded", false);
            }
          })
          .catch(() => {});
      } else {
        this.$confirm("确认删除该分类？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            this.$set(node, "_isDeleted", true);
          })
          .catch(() => {});
      }
    },

    // 删除有子级的分类时，把未删除的子级提升到其上级，并同步修正归属与层级
    promoteChildrenToParent(node, activeChildren) {
      this.$set(
        node,
        "children",
        (node.children || []).filter((c) => c._isDeleted),
      );
      const pid = this.normalizeParentId(node.parentId);
      let siblings = this.treeData;
      if (pid !== -1) {
        const parent = this.findNodeById(pid);
        if (parent && parent.children) siblings = parent.children;
      }
      const idx = siblings.indexOf(node);
      siblings.splice(
        idx < 0 ? siblings.length : idx + 1,
        0,
        ...activeChildren,
      );
      const newParentId = pid === -1 ? -1 : node.parentId;
      activeChildren.forEach((child) => {
        this.$set(child, "parentId", newParentId);
      });
      this.fixLevels(activeChildren, node.level || 1);
    },

    fixLevels(nodes, level) {
      nodes.forEach((n) => {
        this.$set(n, "level", level);
        if (n.children && n.children.length) {
          this.fixLevels(n.children, level + 1);
        }
      });
    },

    onDragStart(event, index) {
      const item = this.flatList[index];
      if (item.node._isNew) {
        event.preventDefault();
        return;
      }
      this.dragIndex = index;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", index);
    },

    onDragOver(event, index) {
      event.preventDefault();
      if (this.dragIndex === -1 || this.dragIndex === index) return;
      const sourceItem = this.flatList[this.dragIndex];
      const targetItem = this.flatList[index];
      if (!sourceItem || !targetItem) return;
      if (sourceItem.depth !== targetItem.depth) return;
      const srcParent = this.normalizeParentId(sourceItem.node.parentId);
      const tgtParent = this.normalizeParentId(targetItem.node.parentId);
      if (srcParent !== tgtParent) return;
      this.dragOverIndex = index;
    },

    normalizeParentId(pid) {
      if (pid === -1 || pid == null) return -1;
      return pid;
    },

    onDrop(event, index) {
      event.preventDefault();
      if (this.dragIndex === -1 || this.dragIndex === index) {
        this.dragIndex = -1;
        this.dragOverIndex = -1;
        return;
      }
      const sourceItem = this.flatList[this.dragIndex];
      const targetItem = this.flatList[index];
      if (!sourceItem || !targetItem) {
        this.dragIndex = -1;
        this.dragOverIndex = -1;
        return;
      }
      if (sourceItem.depth !== targetItem.depth) {
        this.dragIndex = -1;
        this.dragOverIndex = -1;
        return;
      }
      const sourceNode = sourceItem.node;
      const targetNode = targetItem.node;
      const parentId = sourceNode.parentId;

      let children = [];
      if (this.normalizeParentId(parentId) === -1) {
        children = this.treeData;
      } else {
        const parent = this.findNodeById(parentId);
        if (!parent || !parent.children) {
          this.dragIndex = -1;
          this.dragOverIndex = -1;
          return;
        }
        children = parent.children;
      }

      const srcIdx = children.indexOf(sourceNode);
      const tgtIdx = children.indexOf(targetNode);
      if (srcIdx === -1 || tgtIdx === -1) {
        this.dragIndex = -1;
        this.dragOverIndex = -1;
        return;
      }

      const reordered = [...children];
      const [moved] = reordered.splice(srcIdx, 1);
      const newTgtIdx = reordered.indexOf(targetNode);
      reordered.splice(newTgtIdx, 0, moved);

      reordered.forEach((child, i) => {
        this.$set(child, "orderNum", i);
        if (!child._isNew) {
          this.$set(child, "_isModified", true);
        }
      });

      if (this.normalizeParentId(parentId) === -1) {
        this.treeData = reordered;
      } else {
        const parent = this.findNodeById(parentId);
        this.$set(parent, "children", reordered);
      }

      this.treeKey++;
      this.dragIndex = -1;
      this.dragOverIndex = -1;
    },

    onDragEnd() {
      this.dragIndex = -1;
      this.dragOverIndex = -1;
    },

    collectAllNodes(nodes, result, uidToTempId) {
      // 首轮：给新增节点分配负数临时id（从-1000起，避免与根标记-1混淆），
      // 子级新增时parentId是父的_uid字符串，统一映射为该负数id，后端按顺序建映射
      if (!uidToTempId) {
        uidToTempId = {};
        let tempId = -1000;
        const assign = (list) => {
          list.forEach((n) => {
            if (n._isNew && !n._isDeleted) uidToTempId[n._uid] = tempId--;
            if (n.children && n.children.length) assign(n.children);
          });
        };
        assign(nodes);
      }

      nodes.forEach((node) => {
        // 新增后又删除的节点不提交，其子树一并跳过
        if (node._isNew && node._isDeleted) return;

        let parentId = node.parentId;
        if (uidToTempId[parentId] != null) {
          parentId = uidToTempId[parentId];
        }

        result.push({
          id: node._isNew ? uidToTempId[node._uid] : node.id,
          level: node.level,
          parentId,
          className: node.className,
          orderNum: node.orderNum || 0,
          isDel: node._isDeleted && !node._isNew ? 1 : 0,
        });

        if (node.children && node.children.length > 0) {
          this.collectAllNodes(node.children, result, uidToTempId);
        }
      });
    },

    validateTree(nodes) {
      const errors = [];
      const check = (list) => {
        list.forEach((node) => {
          if (node._isDeleted) {
            if (node.children && node.children.length > 0) {
              check(node.children);
            }
            return;
          }
          if (node._editing) {
            errors.push(
              `"${node._originalName ||
                node.className ||
                "未命名"}" 编辑尚未确认`,
            );
          }
          const name = (node.className || "").trim();
          if (!name) {
            errors.push("分类名称不能为空");
          } else if (name.length > 12) {
            errors.push(`"${name}" 超过12个字符限制`);
          }
          if (node.children && node.children.length > 0) {
            check(node.children);
          }
        });
      };
      check(nodes);
      return errors;
    },

    async handleSave() {
      const errors = this.validateTree(this.treeData);
      if (errors.length > 0) {
        this.$message({
          type: "warning",
          message: errors.slice(0, 3).join("；"),
          duration: 3000,
          showClose: true,
        });
        return;
      }

      const allNodes = [];
      this.collectAllNodes(this.treeData, allNodes);

      if (allNodes.length === 0) {
        this.$message({ type: "info", message: "没有分类数据" });
        return;
      }

      this.saving = true;
      const res = await saveClass(allNodes);
      if (res.error.code === 200) {
        this.$message({ type: "success", message: "保存成功" });
        this.$emit("update:dialogVisiClass", false);
        this.$emit("getData");
      } else {
        this.$message({ type: "error", message: res.error.message });
      }
      this.saving = false;
    },

    handleCancel() {
      this.$emit("update:dialogVisiClass", false);
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-body {
  min-height: 30vh;
  max-height: 60vh;
  overflow-y: auto;
}

.add-first-area {
  margin-top: 10px;
  .el-button {
    font-size: 12px;
  }
}

.add-first-input-row {
  display: flex;
  align-items: flex-start;

  padding: 4px 0;

  .add-first-form {
    flex: 1;
    min-width: 0;
    max-width: 400px;

    .el-form-item {
      margin-bottom: 0;
    }

    // 错误文案改为占位布局，避免absolute时压住下一行
    /deep/ .el-form-item__error {
      position: static;
    }
  }

  .action-btns {
    flex-shrink: 0;
    margin-left: 12px;
    white-space: nowrap;
    height: 40px;
    display: inline-flex;
    align-items: center;
  }
}

.edit-form-inline {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;

  .edit-form-item {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
  }

  // 错误文案改为占位布局，避免absolute时压住下一行
  /deep/ .el-form-item__error {
    position: static;
  }
}

.tree-list {
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

// 行内编辑时校验文案会占行高，整行改为顶部对齐，
// 让拖拽柄/展开图标/操作按钮都和输入框在同一水平带
.tree-item.editing {
  align-items: flex-start;

  .drag-handle,
  .expand-icon {
    line-height: 28px; // mini输入框高度
  }

  .actions {
    height: 40px;
    display: inline-flex;
    align-items: center;
  }
}

.tree-item {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
  min-height: 36px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f5f7fa;
  }

  &.drag-source {
    opacity: 0.5;
    background-color: #ecf5ff;
  }

  &.drag-over {
    border-top: 2px solid #409eff;
  }
}

.drag-handle {
  cursor: grab;
  color: #c0c4cc;
  margin-right: 8px;
  font-size: 14px;
  flex-shrink: 0;
  user-select: none;

  &:active {
    cursor: grabbing;
    color: #409eff;
  }

  &.no-drag {
    cursor: not-allowed;
    opacity: 0.3;
    pointer-events: none;
  }
}

.expand-icon {
  width: 18px;
  font-size: 10px;
  color: #909399;
  cursor: pointer;
  flex-shrink: 0;
  text-align: center;
  user-select: none;

  &:hover {
    color: #409eff;
  }

  &.placeholder {
    cursor: default;
  }
}

.inline-input {
  flex: 1;
  margin: 0 8px;
  /deep/ .el-input__inner {
    height: 28px;
    line-height: 28px;
    padding: 0 8px;
  }
}

.node-name {
  flex: 1;
  margin: 0 8px;
  line-height: 30px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;

  &.deleted {
    text-decoration: line-through;
    color: #c0c4cc;
  }
}

.actions {
  flex-shrink: 0;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
  i {
    font-size: 14px;
    color: #909399;
    cursor: pointer;
    padding: 4px;
    border-radius: 2px;

    &:hover {
      color: #409eff;
      background-color: #ecf5ff;
    }

    &.el-icon-delete:hover {
      color: #f56c6c;
      background-color: #fef0f0;
    }
  }
}

.empty-tip {
  text-align: center;
  padding: 40px 0;
  color: #c0c4cc;
  font-size: 14px;
}

.dialog-footer {
  text-align: right;
}
</style>
